import { TRADING_COMPANY } from './report-company';
import type { ReportTableDoc, ReportRow } from './report-table-types';

interface StockLevelExportRow {
  productCode: string;
  productName: string;
  warehouseName: string;
  available: number;
  reserved: number;
  onOrder: number;
  averageCost: number | null;
  stockValue: number | null;
  status: string;
}

interface StockLevelExportData {
  rows: StockLevelExportRow[];
  grandStockValue: number;
  displayCurrency: string;
  // Today-rate used to translate the TRY book values (null for TRY exports).
  fxRate?: number | null;
  fxRateDate?: string | Date | null;
}

const STATUS_LABELS: Record<string, string> = {
  outOfStock: 'Stok Yok',
  belowReorder: 'Sipariş Altı',
  lowStock: 'Düşük Stok',
  inStock: 'Stokta',
};

export function buildStockLevelsDoc(
  data: StockLevelExportData,
  params: string[] = [],
): ReportTableDoc {
  const dataRows: ReportRow[] = data.rows.map((row) => ({
    cells: {
      productCode: row.productCode,
      productName: row.productName,
      warehouseName: row.warehouseName,
      available: row.available,
      reserved: row.reserved,
      onOrder: row.onOrder,
      averageCost: row.averageCost,
      stockValue: row.stockValue,
      status: STATUS_LABELS[row.status] ?? row.status,
    },
  }));

  const totalRow: ReportRow = {
    cells: {
      productCode: '',
      productName: 'GENEL TOPLAM',
      warehouseName: '',
      available: null,
      reserved: null,
      onOrder: null,
      averageCost: null,
      stockValue: data.grandStockValue,
      status: '',
    },
    emphasis: 'total',
  };

  // Costs are frozen TRY book values. A non-TRY export is a TRANSLATION at
  // the stated rate — say so on the document instead of implying a stable
  // foreign-currency book value.
  const fxParams = [...params];
  if (data.displayCurrency !== 'TRY' && data.fxRate) {
    const d = data.fxRateDate ? new Date(data.fxRateDate) : null;
    const dayLabel =
      d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('tr-TR') : 'güncel';
    fxParams.push(
      `Değerler TL defter değerinin çevrimidir — kur: 1 ${data.displayCurrency} = ` +
        `${data.fxRate.toLocaleString('tr-TR')} TL (${dayLabel})`
    );
  }

  return {
    company: TRADING_COMPANY,
    title: `STOK SEVİYELERİ (${data.displayCurrency})`,
    params: fxParams,
    generatedLabel: `Oluşturulma: ${new Date().toLocaleString('tr-TR')}`,
    columns: [
      { key: 'productCode', header: 'Kod', width: 12 },
      { key: 'productName', header: 'Ürün', width: 24 },
      { key: 'warehouseName', header: 'Depo', width: 16 },
      { key: 'available', header: 'Mevcut', num: true, width: 10 },
      { key: 'reserved', header: 'Rezerve', num: true, width: 10 },
      { key: 'onOrder', header: 'Siparişte', num: true, width: 10 },
      { key: 'averageCost', header: 'Ort. Maliyet', num: true, width: 14 },
      { key: 'stockValue', header: 'Stok Değeri', num: true, width: 14 },
      { key: 'status', header: 'Durum', width: 14 },
    ],
    rows: [...dataRows, totalRow],
  };
}
